import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import type { ActiveTab } from "@/appInterface/windows/VoteWindow/types";
import type { InitialData } from "@/canv/types";
import { STICKER_COLORS } from "@/elements/sticker";
import type { LinkPopupViewModel } from "@/elements/text/linkPopup";
import type { UITimerData } from "@/interface/HeaderBar/RightBar/Timer/types";
import { MIDNMAP_LIST_DATA } from "@/interface/SideBar/MindMapMenu/constants";
import type { ContextMenuFlags, HeaderButtonNames } from "@/interface/types";
import type { MultiUser } from "@/multiManager";
import type { WidgetOptions } from "@/shared/types/widgetOptions";
import { CursorTool } from "@/tools/cursorTool";
import { EMOTIONS_PATHS } from "@/tools/emoteTool/emoteTool";
import type { PenToolMode } from "@/tools/PenTool/types";
import type { Tool } from "@/tools/types";
import type { widgetOption } from "@/widgetMenu/widgetMenu";

export const zoomAtom = atom<number>(100);

export const currentToolAtom = atom<Tool>(new CursorTool());

type SearchResultStore = {
    total?: number;
    currentIndex?: number;
};
export const searchResultAtom = atom<SearchResultStore>({
    total: 0,
    currentIndex: 0,
});

type BoardInfoStore = Record<string, any>;

export const boardInfoAtom = atom<InitialData | BoardInfoStore>({});
export const multiCursorsAtom = atom<{
    disableMultiCursors: boolean;
}>({
    disableMultiCursors: false,
});

export const buttonDisabledAtom = atom<Record<string, boolean>>({});

export const usersAtom = atom<{
    users: MultiUser[];
    trackingSession: string | null;
}>({
    users: [],
    trackingSession: null,
});

export const voteTimerVisibilityAtom = atom<boolean>(false);

export const headerActiveButtonAtom = atom<HeaderButtonNames | null>(null);

export const startVoteVisibilityAtom = atom<boolean>(false);

export const startVoteActiveTabAtom = atom<ActiveTab>("new");

export const activeVoteVisibilityAtom = atom<boolean>(false);

export const widgetMenuCoordinatesAtom = atom<{
    top: number;
    left: number;
    canvasMatrixKey: string;
    selectionKey: string;
} | null>(null);

export const widgetMenuAvailableOptions = atom<WidgetOptions[]>([]);

export const widgetMenuOptionsAtom = atom<Map<WidgetOptions, widgetOption[]>>(new Map());

export const widgetMenuOptionsByNameAtom = atomFamily((name: WidgetOptions) =>
    atom((get) => {
        const map = get(widgetMenuOptionsAtom);
        return map.get(name) || [];
    }),
);

export const aiAssistantButtonDisabled = atom<boolean>(false);

export const contextMenuVisibilityAtom = atom<{ isVisible: boolean; anchor?: HTMLButtonElement }>({
    isVisible: false,
    anchor: undefined,
});
export const contextMenuVisibilityItemsAtom = atom<{
    menuItems: ContextMenuFlags;
}>({
    menuItems: {
        copy: false,
        paste: false,
        duplicate: false, //только для презентации
        lock: false, //только для презентации
        unlock: false, //только для презентации
        comment: false,
        toFront: false,
        toBack: false,
        arrange: false,
        redo: false,
        undo: false,
        copyAsPng: false,
        export: false,
        linkToWidget: false,
        delete: false,
        addSticker: false,
        addText: false,
        addShape: false,
    },
});

export const toolBoxVisibility = atom<boolean>(true);
export const moreToolVisibility = atom<boolean>(false);

export const stickerToolMenuVisibility = atom<boolean>(false);
export const stickerToolMenuColor = atom<string>(STICKER_COLORS[0]);

export const arrowToolMenuVisibility = atom<boolean>(false);
export const arrowToolMenuLineType = atom<string>("curved");

export const emoteToolMenuAtomVisibility = atom<boolean>(false);
export const emoteToolMenuAtomActive = atom<string>(EMOTIONS_PATHS[0]);

export const mindMapToolMenuVisibilityAtom = atom<boolean>(false);
export const mindMapToolMenuActivePresetAtom = atom<string>(MIDNMAP_LIST_DATA[0].code);

export const penToolMenuVisibilityAtom = atom<boolean>(false);
export const penToolMenuTypeAtom = atom<PenToolMode>("pen");
export const penToolMenuColorAtom = atom<string>("#000000");
export const penToolMenuLineWidthAtom = atom<number>(6);

export const templateToolMenuVisibility = atom<boolean>(false);

export const timerAtom = atom<UITimerData>({
    duration: 0,
    startedAt: null,
    pausedAt: null,
    updatedAt: null,
    estimatedStopTime: null,
    remainingAfterPause: null,
});

export const minimapUpdateAtom = atom<boolean>(false);

export const presentationScreenVisibility = atom<boolean>(false);
export const presentationUID = atom<string>("");
export const linkInputCoordinatesAtom = atom<{ top: number; left: number } | null>(null);
export const linkInputDefaultValueAtom = atom<string>("");
export const linkPopupStateAtom = atom<LinkPopupViewModel | null>(null);
